import {
  Create,
  SimpleForm,
  TextInput,
  useNotify,
  useRedirect,
  Toolbar,
  SaveButton,
  required,
} from 'react-admin';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { usersProvider } from '../../providers';
import _ from "lodash";
import { useTheme } from '@mui/material/styles';

const CustomToolbar = ({ isLoading }) => {
  const redirect = useRedirect();
  const theme = useTheme();
  const backgroundColor = theme.palette.background.paper;

  const handleCancel = () => {
    redirect('list', 'cognitoUsers');
  };

  return (
    <Toolbar sx={{ backgroundColor }}>
      <Box display="flex" justifyContent="flex-end" width="100%">
        <Button
          variant="text"
          onClick={handleCancel}
          sx={{
            color: theme.palette.common.white,
            borderColor: theme.palette.primary.main,
            marginRight: theme.spacing(1),
            textTransform: 'none',
            minWidth: '95px',
          }}
          size='small'
          disabled={isLoading}
        >
          Cancel
        </Button>
        <SaveButton
          label='Create'
          size='small'
          icon={null}
          sx={{ textTransform: 'none', minWidth: '95px' }}
          disabled={isLoading}
        />
      </Box>
    </Toolbar>
  );
};

const RoleSelect = ({ onChange }) => {
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState([]);

  useEffect(() => {
    const fetchRoles = async () => {
      setLoading(true);
      try {
        const allRoles = await usersProvider("GET", "/listGroups", {});
        setRoles(allRoles.Groups || []);

        const defaultRole = allRoles.Groups[0].GroupName;
        setSelectedRole(defaultRole);
        onChange(defaultRole);
      } catch (error) {
        console.error("RoleSelect Error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRoles();
  }, []);

  const handleChange = (event) => {
    const selectedValues = event.target.value;
    setSelectedRole(selectedValues);
    onChange(selectedValues);
  };

  return (
    <FormControl fullWidth>
      <InputLabel id="role-select-label">Select Role</InputLabel>
      {loading ? (
        <Box display="flex" alignItems="center" justifyContent="center" height={50}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Select
          labelId="role-select-label"
          value={selectedRole}
          onChange={handleChange}
          renderValue={(selected) => `${selected}`}
          style={{ maxHeight: 300, overflowY: "auto" }}
        >
          {roles.map((role) => (
            <MenuItem key={role.GroupName} value={role.GroupName}>
              <ListItemText primary={role.GroupName} />
            </MenuItem>
          ))}
        </Select>
      )}
    </FormControl>
  );
};

const CreateUser = () => {
  const [selectedRole, setSelectedRole] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const notify = useNotify();
  const redirect = useRedirect();

  const handleSuccessCreate = (formData) => {
    setIsLoading(true);
    usersProvider("POST", "/createUser", {
      username: formData.name,
      email: formData.email,
      groupname: [selectedRole],
      password: formData.password,
    }).then(async () => {
      notify('User created successfully', { type: 'success' });
      redirect('list', 'cognitoUsers');
    }).catch(err => {
      console.error('Error creating User:', err);
      notify('Error creating User', { type: 'error' });
    }).finally(() => {
      setIsLoading(false);
    });
  };

  return (
    <Card variant="elevation" sx={{ width: '50%' }}>
      <CardContent>
        <Create title="Create User">
          <SimpleForm onSubmit={handleSuccessCreate} toolbar={<CustomToolbar isLoading={isLoading} />}>
            <Typography variant="subtitle2">User Details</Typography>
            <TextInput source="name" label="NAME" fullWidth validate={[required('Name is required')]} />
            <TextInput source="email" label="EMAIL" fullWidth validate={[required('Name is required')]} />

            <Typography variant="subtitle2">
              Role
            </Typography>
            <RoleSelect onChange={setSelectedRole} />

            <Typography variant="subtitle2" sx={{ marginTop: "15px" }}>
              Password
            </Typography>
            <TextInput
              source="password"
              label="PASSWORD"
              fullWidth
              validate={[required('Name is required')]}
            />
          </SimpleForm>
        </Create>
      </CardContent>
    </Card>
  );
};

export default CreateUser;
export { CreateUser };
